import React from 'react';
import { Box, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { DiagnosisTableData } from '../types';
import DiagnosisRowsTable from './DiagnosisRowsTable';

interface InpatientDischargeDiagnosisTableProps {
  /**
   * The rows, not the HTTP envelope — the same contract `DiagnosisTable` takes. Taking the
   * response meant taking it optionally, and an absent response then rendered the same "nothing
   * recorded" copy as a response that genuinely reported none, two different answers collapsed
   * into one sentence. ChartFactory renders a placeholder when there is no response at all, so
   * that state never reaches here.
   */
  data: DiagnosisTableData;
  title: string;
}

/**
 * SHER-583. The ICD-11 diagnoses recorded on the discharge form, for inpatient episodes that
 * ENDED in the selected period.
 *
 * Deliberately separate from the inpatient diagnosis donut beside it, which answers a different
 * question: that one counts any ICD-coded record in the window, admission notes included. An
 * admission diagnosis is provisional; a discharge diagnosis is the clinical conclusion after the
 * stay, which is what the ticket asks to report on. The subtitle says so on screen, because two
 * diagnosis panels on one tab showing different numbers otherwise reads as a bug.
 *
 * The table is `DiagnosisRowsTable`, shared with the outpatient panel, so the two carry the same
 * columns by construction rather than by agreeing today. What stays here is what genuinely
 * differs: the title, the subtitle, and the empty state — which is not the outpatient one's.
 * Here "empty" usually means the tenant has no ICD-11 field on its discharge form, and saying so
 * is the difference between a configuration note and a suspected defect.
 */
const InpatientDischargeDiagnosisTable: React.FC<InpatientDischargeDiagnosisTableProps> = ({
  data,
  title,
}) => {
  const rows = data?.rows ?? [];

  return (
    <Box>
    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      <Translate id="hospital.analytics.discharge_diagnoses.subtitle" />
    </Typography>

    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        <Translate id="hospital.analytics.discharge_diagnoses.empty" />
      </Typography>
    ) : (
      <DiagnosisRowsTable rows={rows} showDischarges />
    )}
    </Box>
  );
};

export default InpatientDischargeDiagnosisTable;
